// src/Plans.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaCheck,
  FaMoon,
  FaSun,
  FaSpinner,
  FaArrowLeft,
  FaBolt,
} from 'react-icons/fa';
import DiscountStrip from './component/DiscountStrip';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

const palette = {
  primary: '#2563eb',
  purple: '#7c3aed',
  error: '#ef4444',
};

async function fetchPrices() {
  try {
    const res = await fetch(`${API_URL}/api/prices`);
    const data = await res.json();
    return data?.result ?? null;
  } catch {
    return null;
  }
}

function normalizeActiveCampaign(data) {
  if (!data || typeof data !== 'object') return { enabled: false };

  return {
    enabled: data.enabled ?? data.Enabled ?? false,
    campaign: data.campaign ?? data.Campaign ?? null,
    content: data.content ?? data.Content ?? null,
    capacity: data.capacity ?? data.Capacity ?? null,
  };
}

function getPriceValue(priceItem) {
  if (priceItem == null) return 0;
  if (typeof priceItem === 'number') return Number(priceItem);
  if (typeof priceItem === 'object') return Number(priceItem.price ?? 0);
  return 0;
}

function getCompareAtValue(priceItem) {
  if (priceItem == null) return null;
  if (typeof priceItem === 'object') {
    const v = priceItem.compareAtPrice;
    return v == null ? null : Number(v);
  }
  return null;
}

function formatTomanFa(n) {
  return `${Number(n || 0).toLocaleString('fa-IR')} تومان`;
}

function normalizeDigits(value) {
  return String(value ?? '')
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
}

function extractCampaignPrice(activeCampaign) {
  const content = activeCampaign?.content ?? activeCampaign?.Content ?? null;
  const campaign = activeCampaign?.campaign ?? activeCampaign?.Campaign ?? null;

  const candidates = [
    content?.price,
    content?.Price,
    content?.finalPrice,
    content?.FinalPrice,
    content?.discountPrice,
    content?.DiscountPrice,
    campaign?.price,
    campaign?.Price,
    content?.priceText,
    content?.PriceText,
  ];

  for (const candidate of candidates) {
    if (candidate == null) continue;

    if (typeof candidate === 'number' && candidate > 0) return candidate;

    if (typeof candidate === 'string') {
      const normalized = normalizeDigits(candidate);
      const digitsOnly = normalized.replace(/[^\d]/g, '');
      const parsed = Number(digitsOnly);

      if (parsed > 0) return parsed;
    }
  }

  return 0;
}

function PriceView({ item, accentColor }) {
  const price = getPriceValue(item);
  const compareAt = getCompareAtValue(item);

  if (!price) {
    return <div className="text-sm text-slate-400">قیمت نامشخص</div>;
  }

  return (
    <div className="text-right">
      {compareAt > price && (
        <div className="text-sm text-slate-400 line-through mb-1">
          {formatTomanFa(compareAt)}
        </div>
      )}

      <div className="text-2xl font-black" style={{ color: accentColor }}>
        {formatTomanFa(price)}
      </div>
    </div>
  );
}

export default function Plans() {
  const navigate = useNavigate();
  const location = useLocation();

  const [dark, setDark] = useState(true);

  const [prices, setPrices] = useState(null);
  const [loadingPrices, setLoadingPrices] = useState(true);

  const [campaignLoading, setCampaignLoading] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState({ enabled: false });
  const [campaignError, setCampaignError] = useState(null);

  const routeState = location?.state || {};
  const forceShowRegularPlans = !!routeState?.forceShowRegularPlans;
  const preselectedPlan = Number(routeState?.preselectedPlan || 0);

  const colors = useMemo(() => {
    if (dark) {
      return {
        bg: '#070B14',
        bg2: '#08101C',
        bg3: '#0A1224',
        card: 'rgba(21,29,51,0.96)',
        card2: 'rgba(14,20,37,0.96)',
        border: 'rgba(255,255,255,0.08)',
        innerCard: '#121A2E',
        text: '#FFFFFF',
        sub: '#B7C0D1',
        muted: '#8FA0BA',
        headerBg: 'rgba(10,15,30,0.80)',
      };
    }

    return {
      bg: '#F8FAFF',
      bg2: '#F2F6FF',
      bg3: '#EDF2FF',
      card: 'rgba(255,255,255,0.96)',
      card2: 'rgba(245,248,255,0.96)',
      border: 'rgba(15,23,42,0.10)',
      innerCard: '#F8FAFC',
      text: '#0F172A',
      sub: '#475569',
      muted: '#64748B',
      headerBg: 'rgba(255,255,255,0.82)',
    };
  }, [dark]);

  useEffect(() => {
    (async () => {
      const p = await fetchPrices();
      setPrices(p);
      setLoadingPrices(false);
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setCampaignLoading(true);
        setCampaignError(null);

        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        const res = await fetch(`${API_URL}/api/campaigns/active?placement=summary`, {
          headers,
        });

        if (!res.ok) {
          if (!cancelled) {
            setActiveCampaign({ enabled: false });
            setCampaignError(`campaign_http_${res.status}`);
          }
          return;
        }

        const data = await res.json();
        const mapped = normalizeActiveCampaign(data);

        if (!cancelled) setActiveCampaign(mapped);
      } catch {
        if (!cancelled) {
          setActiveCampaign({ enabled: false });
          setCampaignError('campaign_exception');
        }
      } finally {
        if (!cancelled) setCampaignLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const showCampaign =
    !forceShowRegularPlans &&
    activeCampaign?.enabled === true &&
    !!activeCampaign?.content;

  const campaignMeta = {
    campaignId: activeCampaign?.campaign?.id ?? activeCampaign?.campaign?.Id ?? null,
    campaignKey: activeCampaign?.campaign?.key ?? activeCampaign?.campaign?.Key ?? null,
  };

  const campaignPrice = extractCampaignPrice(activeCampaign);

  const planCards = useMemo(
    () => [
      {
        id: 1,
        requestedPlan: 1,
        title: 'برنامه تمرینی',
        subtitle: 'برای کسانی که فقط برنامه تمرین اختصاصی می‌خواهند.',
        priceItem: prices?.normal?.workout,
        badge: null,
        accentColors: ['#60a5fa', '#2563eb'],
        features: [
          'برنامه تمرینی متناسب با هدف و سطح',
          'مناسب تمرین در خانه یا باشگاه',
          'تعداد جلسات بر اساس انتخاب تو',
          'ساختار قابل اجرا و مرحله‌به‌مرحله',
        ],
      },
      {
        id: 2,
        requestedPlan: 2,
        title: 'برنامه رژیم و تغذیه',
        subtitle: 'برای کنترل تغذیه، کاهش وزن یا عضله‌سازی اصولی.',
        priceItem: prices?.normal?.diet,
        badge: null,
        accentColors: ['#10b981', '#059669'],
        features: [
          'رژیم غذایی متناسب با هدف',
          'وعده‌های قابل اجرا با سبک زندگی ایرانی',
          'گزینه‌های جایگزین برای انعطاف بیشتر',
          'مناسب کاهش وزن یا افزایش عضله',
        ],
      },
      {
        id: 3,
        requestedPlan: 3,
        title: 'برنامه تمرین و رژیم غذایی',
        subtitle: 'کامل‌ترین انتخاب برای نتیجه بهتر و مسیر منسجم‌تر.',
        priceItem: prices?.normal?.full,
        badge: 'پیشنهادی',
        accentColors: ['#a78bfa', '#7c3aed'],
        recommended: true,
        features: [
          'برنامه تمرین + رژیم غذایی کامل',
          'هماهنگی تمرین و تغذیه برای نتیجه بهتر',
          'بهترین گزینه برای شروع جدی',
          'ارزش خرید بالاتر نسبت به خرید جداگانه',
        ],
      },
    ],
    [prices]
  );

  const trackCampaignClick = async ({ campaignId, campaignKey }) => {
    try {
      const token = localStorage.getItem('token');

      await fetch(`${API_URL}/api/campaigns/track/click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          campaignId: campaignId ?? null,
          campaignKey: campaignKey ?? null,
          placement: 'plans',
        }),
      });
    } catch {}
  };

  const goToWizard = ({ campaignId, campaignKey, campaignPrice }) => {
    const token = localStorage.getItem('token');

    const navState = {
      campaignId: campaignId ?? null,
      campaignKey: campaignKey ?? null,
      campaignPrice: campaignPrice ?? 0,
      prices: prices ?? null,
    };

    localStorage.setItem(
      'redirectAfterLogin',
      JSON.stringify({
        to: '/PlanWizard',
        state: navState,
      })
    );

    localStorage.setItem('planWizardState', JSON.stringify(navState));

    if (!token) {
      navigate('/login');
      return;
    }

    navigate('/PlanWizard', { state: navState });
  };

  const handleCampaignPress = async () => {
    await trackCampaignClick(campaignMeta);

    const fallbackPrice = getPriceValue(prices?.normal?.full);
    const finalCampaignPrice = campaignPrice > 0 ? campaignPrice : fallbackPrice;

    goToWizard({
      campaignId: campaignMeta.campaignId,
      campaignKey: campaignMeta.campaignKey,
      campaignPrice: finalCampaignPrice,
    });
  };

  const handleStartRegular = () => {
    goToWizard({
      campaignId: null,
      campaignKey: null,
      campaignPrice: 0,
    });
  };

  return (
    <div dir="rtl" className={dark ? 'dark' : ''}>
      <div
        className="min-h-screen font-vazir relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.bg}, ${colors.bg2}, ${colors.bg3})`,
          color: colors.text,
        }}
      >
        <div className="fixed top-20 right-[-80px] w-72 h-72 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />
        <div className="fixed bottom-20 left-[-80px] w-72 h-72 rounded-full bg-purple-500/20 blur-3xl pointer-events-none" />

        <DiscountStrip />

        <header className="relative z-20 max-w-7xl mx-auto px-4 pt-4">
          <div
            className="min-h-[64px] rounded-3xl border px-4 flex items-center justify-between backdrop-blur-xl shadow-lg"
            style={{
              backgroundColor: colors.headerBg,
              borderColor: colors.border,
            }}
          >
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex items-center gap-3"
            >
              <img src="/logo.png" alt="Mogym" className="w-11 h-11 rounded-2xl" />
              <div className="text-right">
                <div className="font-black text-lg">انتخاب برنامه</div>
                <div className="hidden sm:block text-xs mt-1" style={{ color: colors.muted }}>
                  تمرین، تغذیه یا پکیج کامل
                </div>
              </div>
            </button>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="hidden sm:inline-flex px-4 py-2 rounded-2xl border text-sm font-bold"
                style={{
                  borderColor: colors.border,
                  backgroundColor: colors.innerCard,
                  color: colors.text,
                }}
              >
                صفحه اصلی
              </button>

              <button
                type="button"
                onClick={() => setDark((v) => !v)}
                className="w-11 h-11 rounded-2xl border flex items-center justify-center"
                style={{
                  background: dark
                    ? 'linear-gradient(135deg,#111827,#1F2937)'
                    : 'linear-gradient(135deg,#FFFFFF,#F1F5F9)',
                  borderColor: colors.border,
                  color: colors.text,
                }}
              >
                {dark ? <FaSun /> : <FaMoon />}
              </button>
            </div>
          </div>
        </header>

        <main className="relative z-10 max-w-7xl mx-auto px-4 py-8 pb-32">
          <HeroSection colors={colors} showCampaign={showCampaign} />

          {campaignLoading ? (
            <LoadingBox colors={colors} text="در حال دریافت اطلاعات کمپین…" />
          ) : showCampaign ? (
            <CampaignCard
              activeCampaign={activeCampaign}
              colors={colors}
              onPress={handleCampaignPress}
            />
          ) : (
            <>
              {loadingPrices && <LoadingBox colors={colors} text="در حال دریافت قیمت‌ها…" />}

              {!loadingPrices && !prices && (
                <div
                  className="rounded-3xl border p-5 text-center font-bold"
                  style={{
                    borderColor: 'rgba(239,68,68,0.25)',
                    backgroundColor: dark ? 'rgba(127,29,29,0.20)' : 'rgba(254,242,242,0.96)',
                    color: palette.error,
                  }}
                >
                  مشکلی در دریافت قیمت‌ها رخ داد.
                </div>
              )}

              {!!prices && (
                <>
                  <section className="grid lg:grid-cols-3 gap-5 mt-6">
                    {planCards.map((plan) => (
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        colors={colors}
                        isPreselected={preselectedPlan === plan.requestedPlan}
                      />
                    ))}
                  </section>

                  <FlowGuideCard colors={colors} />

                  <StickyActionCard colors={colors} onPress={handleStartRegular} />
                </>
              )}
            </>
          )}

          {!showCampaign && !!campaignError && (
            <p className="mt-5 text-center text-xs" style={{ color: colors.muted }}>
              در حال حاضر کمپین فعالی نمایش داده نمی‌شود.
            </p>
          )}
        </main>
      </div>
    </div>
  );
}

function HeroSection({ colors, showCampaign }) {
  return (
    <section
      className="rounded-[28px] border p-5 md:p-8 shadow-2xl backdrop-blur-xl"
      style={{
        borderColor: colors.border,
        background:
          'linear-gradient(135deg, rgba(37,99,235,0.18), rgba(124,58,237,0.12))',
      }}
    >
      <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-blue-100 text-xs font-bold mb-4">
            <FaBolt />
            قیمت شفاف و شروع سریع
          </div>

          <h1 className="text-3xl md:text-5xl font-black leading-[1.35]">
            برنامه مناسب خودتو انتخاب کن
          </h1>

          <p className="mt-4 text-sm md:text-base leading-8" style={{ color: colors.sub }}>
            {showCampaign
              ? 'پیشنهاد ویژه فعاله. بعد از شروع، داخل مراحل انتخاب می‌کنی مستقیم برنامه کامل بگیری یا اول نمونه‌اش رو ببینی.'
              : 'اول قیمت پلن‌ها رو ببین، بعد وارد مراحل ساخت برنامه شو و داخل مسیر، نوع شروع خودت رو انتخاب کن.'}
          </p>

          <div className="flex flex-wrap gap-2 mt-5">
            <HeroPill text="قیمت‌ها شفاف" />
            <HeroPill text="شخصی‌سازی‌شده" />
            <HeroPill text="خانه یا باشگاه" />
            <HeroPill text="تمرین + تغذیه" />
          </div>
        </div>

        <div
          className="rounded-3xl border p-4"
          style={{
            borderColor: colors.border,
            backgroundColor: 'rgba(37,99,235,0.08)',
          }}
        >
          <MiniOption text="دریافت مستقیم برنامه کامل" colors={colors} />
          <MiniOption text="یا مشاهده نمونه قبل از خرید" colors={colors} />
          <MiniOption text="ادامه مسیر در چند قدم ساده" colors={colors} />
        </div>
      </div>
    </section>
  );
}

function HeroPill({ text }) {
  return (
    <span className="rounded-full px-3 py-1.5 bg-white/10 text-blue-100 text-xs font-bold">
      {text}
    </span>
  );
}

function MiniOption({ text, colors }) {
  return (
    <div
      className="rounded-2xl px-4 py-3 mb-3 last:mb-0 text-sm font-bold"
      style={{
        color: colors.text,
        backgroundColor: 'rgba(255,255,255,0.04)',
      }}
    >
      {text}
    </div>
  );
}

function PlanCard({ plan, colors, isPreselected }) {
  const accent1 = plan.accentColors[0];
  const accent2 = plan.accentColors[1];

  return (
    <article
      className={`relative rounded-[28px] border p-5 md:p-6 shadow-2xl backdrop-blur-xl transition ${
        plan.recommended ? 'lg:-translate-y-2' : ''
      }`}
      style={{
        background: plan.recommended
          ? `linear-gradient(135deg, rgba(124,58,237,0.18), ${colors.card2})`
          : `linear-gradient(135deg, ${colors.card}, ${colors.card2})`,
        borderColor: plan.recommended || isPreselected ? 'rgba(124,58,237,0.45)' : colors.border,
      }}
    >
      {plan.badge && (
        <div
          className="inline-flex rounded-full px-3 py-1 text-xs font-black text-white mb-4"
          style={{
            background: `linear-gradient(135deg, ${accent1}, ${accent2})`,
          }}
        >
          {plan.badge}
        </div>
      )}

      {isPreselected && !plan.badge && (
        <div className="inline-flex rounded-full px-3 py-1 text-xs font-black text-white bg-blue-600 mb-4">
          انتخاب‌شده
        </div>
      )}

      <h2 className="text-xl md:text-2xl font-black">{plan.title}</h2>

      <p className="mt-3 text-sm leading-7" style={{ color: colors.sub }}>
        {plan.subtitle}
      </p>

      <div className="mt-5 space-y-3">
        {plan.features.map((feature) => (
          <div key={feature} className="flex items-start gap-2 text-sm leading-7">
            <span
              className="mt-1 w-5 h-5 rounded-full flex items-center justify-center shrink-0"
              style={{
                backgroundColor: 'rgba(37,99,235,0.15)',
                color: '#60a5fa',
              }}
            >
              <FaCheck className="text-[10px]" />
            </span>

            <span>{feature}</span>
          </div>
        ))}
      </div>

      <div
        className="mt-6 rounded-3xl border p-4"
        style={{
          borderColor: colors.border,
          backgroundColor: colors.innerCard,
        }}
      >
        <div className="text-xs mb-2" style={{ color: colors.muted }}>
          قیمت
        </div>

        <PriceView item={plan.priceItem} accentColor={accent1} />
      </div>
    </article>
  );
}

function FlowGuideCard({ colors }) {
  return (
    <section
      className="mt-6 rounded-[28px] border p-5 md:p-6"
      style={{
        borderColor: colors.border,
        backgroundColor: colors.innerCard,
      }}
    >
      <h2 className="font-black text-lg mb-4">مسیر خیلی ساده است</h2>

      <div className="grid md:grid-cols-3 gap-3">
        <FlowItem index="۱" text="قیمت پلن‌ها را ببین" colors={colors} />
        <FlowItem index="۲" text="وارد مراحل ساخت برنامه شو" colors={colors} />
        <FlowItem
          index="۳"
          text="داخل مسیر انتخاب می‌کنی برنامه کامل می‌خوای یا اول نمونه‌اش را ببینی"
          colors={colors}
        />
      </div>
    </section>
  );
}

function FlowItem({ index, text, colors }) {
  return (
    <div
      className="rounded-3xl border p-4"
      style={{
        borderColor: colors.border,
        backgroundColor: 'rgba(255,255,255,0.035)',
      }}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center font-black mb-3"
        style={{
          backgroundColor: 'rgba(37,99,235,0.18)',
          color: '#93c5fd',
        }}
      >
        {index}
      </div>

      <p className="text-sm leading-7" style={{ color: colors.sub }}>
        {text}
      </p>
    </div>
  );
}

function StickyActionCard({ colors, onPress }) {
  return (
    <div className="fixed bottom-4 left-0 right-0 z-40 px-4 pointer-events-none">
      <div
        className="max-w-3xl mx-auto rounded-[26px] border p-4 shadow-2xl backdrop-blur-xl pointer-events-auto"
        style={{
          borderColor: colors.border,
          backgroundColor: 'rgba(8,12,22,0.92)',
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-right">
            <h3 className="font-black text-white">آماده‌ای وارد ساخت برنامه بشی؟</h3>
            <p className="mt-1 text-xs md:text-sm leading-6 text-slate-300">
              داخل مراحل بعدی می‌تونی انتخاب کنی مستقیم برنامه کامل بگیری یا اول نمونه‌اش را ببینی.
            </p>
          </div>

          <button
            type="button"
            onClick={onPress}
            className="shrink-0 h-13 px-7 py-3.5 rounded-2xl text-white font-black flex items-center justify-center gap-2 transition hover:scale-[1.02]"
            style={{
              background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
            }}
          >
            شروع ساخت برنامه
            <FaArrowLeft />
          </button>
        </div>
      </div>
    </div>
  );
}

function CampaignCard({ activeCampaign, colors, onPress }) {
  const content = activeCampaign?.content || {};
  const capacity = activeCampaign?.capacity || {};

  return (
    <section
      className="mt-6 rounded-[28px] border p-5 md:p-7 shadow-2xl"
      style={{
        borderColor: colors.border,
        background: `linear-gradient(135deg, rgba(124,58,237,0.20), ${colors.card2})`,
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <span className="rounded-full px-3 py-1.5 bg-purple-600 text-white text-xs font-black">
          پیشنهاد ویژه
        </span>

        {capacity?.remainingText && (
          <span className="text-sm" style={{ color: colors.sub }}>
            {capacity.remainingText}
          </span>
        )}
      </div>

      <h2 className="text-2xl md:text-4xl font-black leading-[1.4]">
        {content?.title || content?.headline || 'پیشنهاد ویژه موجیم'}
      </h2>

      {(content?.subtitle || content?.subTitle || content?.description || content?.bodyText) && (
        <p className="mt-4 text-sm md:text-base leading-8" style={{ color: colors.sub }}>
          {content?.subtitle || content?.subTitle || content?.description || content?.bodyText}
        </p>
      )}

      {content?.priceText && (
        <div className="mt-5 text-2xl font-black text-purple-300">
          {content.priceText}
        </div>
      )}

      <button
        type="button"
        onClick={onPress}
        className="mt-6 w-full md:w-auto px-8 py-4 rounded-2xl text-white font-black transition hover:scale-[1.02]"
        style={{
          background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
        }}
      >
        {content?.ctaText || 'شروع ساخت برنامه'}
      </button>
    </section>
  );
}

function LoadingBox({ colors, text }) {
  return (
    <div className="py-14 flex flex-col items-center justify-center">
      <FaSpinner className="animate-spin text-4xl" style={{ color: palette.primary }} />
      <p className="mt-4 text-sm" style={{ color: colors.sub }}>
        {text}
      </p>
    </div>
  );
}